import { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox, CheckboxChangeEventType, Input, ModalDialog } from '@myosh/odin-components';
import { ViewNameOption } from './view-dropdown';
import {
  PostViewExtended,
  PutPatchViewInterface,
  useCreateViewMutation,
  useGetAllViewsQuery,
  useUpdateViewMutation,
} from '../../../redux/config/views';
import { useAppDispatch } from '../../../redux/hooks';
import { setSelectedView } from '../../../redux/slices/selected-view';
import { useUser } from '@myosh/myosh-login';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import { useSelectedView } from '../../Charts/hooks/use-selected-view';
import { ManageViewsModalProps } from './view-handler.component';
import { DateFilterRangeType, useGetUserAppliedFiltersQuery } from '../../../redux/config/filters-api';
import { isEqual } from 'lodash';
import { getDashboardFiltersCombination } from '../../../helpers/dashboard-filter-formatter';

export const ViewSaveModal = ({ visible, onClose, viewSaved }: ManageViewsModalProps) => {
  const [viewsOptions, setViewOptions] = useState<Array<ViewNameOption>>([]);
  const { data, isFetching } = useGetAllViewsQuery();
  const navigateToView = useRef<boolean>(true);
  const [addView] = useCreateViewMutation();
  const [updateView] = useUpdateViewMutation();
  const dispatch = useAppDispatch();
  const { data: userAppliedFilters, isFetching: isFetchingUserAppliedFilters } = useGetUserAppliedFiltersQuery();
  const user = useUser();
  const applicationView = useSelectedView();
  const newViewName = useRef<string>();
  const [isGlobalView, setIsGlobalView] = useState<boolean>();

  useEffect(() => {
    if (applicationView && visible) {
      newViewName.current = applicationView?.name ?? '';
      setIsGlobalView(!applicationView.privateView);
    }
  }, [applicationView, visible]);

  useEffect(() => {
    if (data && !isFetching) {
      const viewOptions = data.map((view) => {
        return {
          text: view.name,
          value: view.id,
          defaultView: view.defaultView,
          ...view,
        };
      });
      setViewOptions(viewOptions);
    }
  }, [data, isFetching]);

  const updateViewHandler = useCallback(
    (updatedView: PutPatchViewInterface) => {
      updateView(updatedView)
        .unwrap()
        .then((result) => {
          if (result) {
            dispatch(setSelectedView({ id: result }));
            toast.success(t('updateViewSuccess'));
          } else {
            toast.error(t('updateViewFailure'));
          }
        })
        .catch(() => {
          toast.error(t('updateViewFailure'));
        });
    },
    [dispatch, updateView]
  );

  const addViewHandler = useCallback(
    (newView: PostViewExtended) => {
      navigateToView.current = true;
      addView(newView)
        .unwrap()
        .then((result) => {
          if (result) {
            dispatch(setSelectedView({ id: result }));
            toast.success(t('viewCreated'), { duration: 3000 });
          }
        });
    },
    [addView, dispatch]
  );
  /**
   * This method takes care of updating an existing view or creating a new one.
   * - Clicking save when view name is not updated and no changes done --> nothing happens
   * - Clicking save with the same name, updates the view
   * - Clicking save with a new name provided
   */
  const saveChangesCallback = () => {
    if (newViewName.current) {
      if (newViewName.current === 'Default') {
        toast.error(t('defaultErrorUpdate'));
        return;
      }

      if (
        newViewName?.current?.trim() === applicationView?.name?.trim() &&
        isEqual(userAppliedFilters?.dashboardFiltersCombination, applicationView?.dashboardFiltersCombination) &&
        isEqual(applicationView.privateView ?? false, !isGlobalView)
      ) {
        return;
      }
      const existingView = viewsOptions.find((viewOption) => viewOption?.name?.trim() === newViewName.current?.trim());
      const payload: PostViewExtended = {
        name: newViewName?.current?.trim(),
        dashboardFiltersCombination: getDashboardFiltersCombination({
          dateFilterRangeType: userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView
            ?.dateFilterRangeType as DateFilterRangeType,
          fromDate: userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView?.fromDate,
          toDate: userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView?.toDate,
          hierarchyValues: userAppliedFilters?.dashboardFiltersCombination?.hierarchyFilters?.hierarchyValues,
          showArchived: userAppliedFilters?.dashboardFiltersCombination?.hierarchyFilters?.showArchived,
        }),
      };

      payload.privateView = !isGlobalView;
      if (existingView) {
        navigateToView.current = false;
        updateViewHandler({ id: existingView.id, ...payload });
        viewSaved?.();
      } else {
        payload.userId = user.state.user?.preferred_username ?? '';
        payload.privateView = !isGlobalView;
        payload.defaultView = false;
        addViewHandler(payload);
        viewSaved?.();
      }
    } else {
      toast.error(t('nameRequired'), { duration: 3000 });
    }
  };

  const handleViewNameChange = (value?: string) => {
    newViewName.current = value;
  };

  const handleIsGlobalChange = (value?: CheckboxChangeEventType) => {
    setIsGlobalView(!!value?.checked);
  };

  return (
    <>
      <ModalDialog
        header={t('saveCurrentView')}
        visible={visible}
        hidden={onClose}
        buttons={[
          {
            type: 'primary',
            name: 'save',
            text: t('save'),
            onClick: saveChangesCallback,
            disabled: isFetchingUserAppliedFilters,
          },
        ]}
        buttonType="submit"
        minDialogWidth={350}
        maxDialogWidth={350}
      >
        <div className="my-2 flex flex-col gap-4">
          <Input
            name="viewName"
            label="Name"
            inputStyles="rounded py-2"
            value={newViewName.current}
            onChange={handleViewNameChange}
          />
          <Checkbox
            name="globalView"
            label={t('globalView')}
            className="rounded"
            initialChecked={isGlobalView}
            value={String(isGlobalView)}
            onChange={handleIsGlobalChange}
          />
        </div>
      </ModalDialog>
    </>
  );
};
